import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Input, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { LoginRecordService } from "../../services";
import { LoginRecord, LoginRecordQueryParams } from "../../types/loginRecord";
import ErrorHandler from "../../utils/errorHandler";
import { ConfirmDialog, EmptyState, Icon, Pager, PersonCell, StatusBadge, Surface, ToolbarGroup, formatTime } from "../../components/ui";
import "./LoginRecord.scss";

const { RangePicker } = DatePicker;

type Query = { keyword: string; range: [Dayjs, Dayjs] | null; page: number; limit: number };

const METHOD_LABEL: Record<string, string> = { sms: "短信验证码", password: "账号密码" };

const sessionBadge = (record: LoginRecord) => {
  if (record.isActive) return <StatusBadge tone="sage">在线</StatusBadge>;
  if (record.tokenExpiresAt && dayjs(record.tokenExpiresAt).isBefore(dayjs())) return <StatusBadge tone="ink">已过期</StatusBadge>;
  return <StatusBadge tone="ink">已下线</StatusBadge>;
};

const LoginRecordList: React.FC = () => {
  const [records, setRecords] = useState<LoginRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<Query>({ keyword: "", range: null, page: 1, limit: 20 });
  const [keywordInput, setKeywordInput] = useState("");
  const [kickTarget, setKickTarget] = useState<LoginRecord | null>(null);
  const [kicking, setKicking] = useState(false);

  const loadRecords = useCallback(async (q: Query) => {
    setLoading(true);
    try {
      const params: LoginRecordQueryParams = { page: q.page, limit: q.limit };
      if (q.keyword) {
        if (/^1[3-9]\d{9}$/.test(q.keyword)) params.phone = q.keyword;
        else params.accountId = Number(q.keyword);
      }
      if (q.range) {
        params.startDate = q.range[0].format("YYYY-MM-DD");
        params.endDate = q.range[1].format("YYYY-MM-DD");
      }
      const response = await LoginRecordService.getLoginRecords(params);
      if (response.code === 0 && response.data) {
        setRecords(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.msg || "获取登录记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords(query);
  }, [query, loadRecords]);

  const applyKeyword = (value: string) => {
    const keyword = value.trim();
    if (keyword && !/^\d+$/.test(keyword)) {
      message.warning("请输入账号 ID 或 11 位手机号");
      return;
    }
    setQuery((prev) => ({ ...prev, keyword, page: 1 }));
  };

  const resetFilters = () => {
    setKeywordInput("");
    setQuery((prev) => ({ ...prev, keyword: "", range: null, page: 1 }));
  };

  const confirmKick = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      const response = await LoginRecordService.kickout(kickTarget.id);
      if (response.code === 0) {
        message.success("已成功踢下线");
        setKickTarget(null);
        loadRecords(query);
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setKicking(false);
    }
  };

  const filtered = !!(query.keyword || query.range);
  const device = (record: LoginRecord) => [record.operatingSystem, record.browserName].filter(Boolean).join(" · ") || record.userAgent || "—";

  const columns: ColumnsType<LoginRecord> = [
    {
      title: "账号",
      key: "account",
      width: 210,
      render: (_, record) => <PersonCell name={record.account?.username || (record.account ? undefined : "未知用户")} sub={record.account?.phone || `ID ${record.accountId}`} />,
    },
    { title: "登录方式", dataIndex: "loginMethod", key: "loginMethod", width: 120, render: (method: string) => METHOD_LABEL[method] || method || "—" },
    { title: "IP 地址", dataIndex: "ipAddress", key: "ipAddress", width: 150, render: (ip: string) => <span className="mono">{ip || "—"}</span> },
    { title: "设备", key: "device", ellipsis: true, render: (_, record) => <span title={record.userAgent}>{device(record)}</span> },
    {
      title: "登录时间 / 有效期",
      key: "time",
      width: 200,
      render: (_, record) => (
        <span className="ddp-two-line">
          <span className="mono">{formatTime(record.createdAt)}</span>
          {record.tokenExpiresAt && <small className="mono">至 {formatTime(record.tokenExpiresAt)}</small>}
        </span>
      ),
    },
    { title: "会话", key: "session", width: 100, render: (_, record) => sessionBadge(record) },
    {
      title: "操作",
      key: "action",
      width: 90,
      align: "right",
      render: (_, record) =>
        record.isActive ? (
          <button type="button" className="ddp-link" onClick={() => setKickTarget(record)}>
            踢下线
          </button>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
  ];

  const target = kickTarget;

  return (
    <>
      <Surface
        toolbar={
          <>
            <ToolbarGroup>
              <Input
                className="login-record__search"
                allowClear
                prefix={<Icon name="search" />}
                placeholder="账号 ID 或手机号"
                aria-label="账号 ID 或手机号"
                value={keywordInput}
                onChange={(e) => {
                  setKeywordInput(e.target.value);
                  if (!e.target.value && query.keyword) applyKeyword("");
                }}
                onPressEnter={() => applyKeyword(keywordInput)}
              />
              <RangePicker
                aria-label="登录时间范围"
                value={query.range}
                onChange={(value) => setQuery((prev) => ({ ...prev, range: value && value[0] && value[1] ? [value[0], value[1]] : null, page: 1 }))}
              />
            </ToolbarGroup>
            <ToolbarGroup>
              <Button type="text" onClick={resetFilters} disabled={!filtered}>
                重置
              </Button>
            </ToolbarGroup>
          </>
        }
        footer={<Pager total={total} current={query.page} pageSize={query.limit} onChange={(page, limit) => setQuery((prev) => ({ ...prev, page, limit }))} />}
      >
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1080 }}
          rowClassName={(record) => (kickTarget?.id === record.id ? "ant-table-row-selected" : "")}
          locale={{
            emptyText: (
              <EmptyState
                icon="loginRec"
                title={filtered ? "没有找到登录记录" : "暂无登录记录"}
                desc={filtered ? "检查账号 ID 或手机号，或放宽时间范围。" : "账号登录三端后，会在这里留下记录。"}
                action={filtered ? <Button onClick={resetFilters}>清空筛选</Button> : undefined}
              />
            ),
          }}
        />
      </Surface>

      <ConfirmDialog
        open={!!target}
        icon="logout"
        title={`将 ${target?.account?.username || target?.account?.phone || "该账号"} 踢下线？`}
        confirmText="踢下线"
        confirmIcon="logout"
        loading={kicking}
        onConfirm={confirmKick}
        onCancel={() => setKickTarget(null)}
      >
        <p>该会话会立即失效，对方需要重新登录。</p>
        {target && (
          <dl className="ddp-kv">
            <dt>账号</dt>
            <dd className="mono">{target.account?.phone || `ID ${target.accountId}`}</dd>
            <dt>IP</dt>
            <dd className="mono">{target.ipAddress || "—"}</dd>
            <dt>设备</dt>
            <dd>{device(target)}</dd>
          </dl>
        )}
      </ConfirmDialog>
    </>
  );
};

export default LoginRecordList;
